import React, { useState } from 'react'
import { DocumentTextIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'

const Generator: React.FC = () => {
  const [formData, setFormData] = useState({
    websiteName: '',
    websiteUrl: '',
    description: '',
    language: 'en',
    allowTraining: 'yes',
    allowCrawling: 'yes',
    contactEmail: '',
    additionalRules: ''
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const generateLlmstxt = () => {
    const content = `# llms.txt for ${formData.websiteName}
# Generated by llms.txt Generator by AndaLabX

Website: ${formData.websiteUrl}
Language: ${formData.language}
Description: ${formData.description}

# Training and Crawling Rules
Allow Training: ${formData.allowTraining}
Allow Crawling: ${formData.allowCrawling}

# Contact Information
Contact: ${formData.contactEmail}

# Additional Rules
${formData.additionalRules}
`
    return content
  }

  const handleDownload = () => {
    const content = generateLlmstxt()
    const blob = new Blob([content], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'llms.txt'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  return (
    <main className="py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold tracking-tight text-brand-black sm:text-4xl font-['Arial']">
              llmstxt Generator
            </h1>
            <p className="mt-4 text-lg text-neutral-400">
              Create a properly formatted llms.txt file for your website using our free generator tool
            </p>
          </div>

          <div className="bg-surface-light border border-neutral-200 rounded-2xl p-4 sm:p-8">
            <form className="space-y-6 sm:space-y-8">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="websiteName" className="block text-sm font-medium text-brand-black">
                    Website Name
                  </label>
                  <input
                    type="text"
                    name="websiteName"
                    id="websiteName"
                    value={formData.websiteName}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md bg-surface-light border-neutral-200 text-brand-black shadow-sm focus:border-brand-black focus:ring-brand-black/10 sm:text-sm"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="websiteUrl" className="block text-sm font-medium text-brand-black">
                    Website URL
                  </label>
                  <input
                    type="url"
                    name="websiteUrl"
                    id="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md bg-surface-light border-neutral-200 text-brand-black shadow-sm focus:border-brand-black focus:ring-brand-black/10 sm:text-sm"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-brand-black">
                  Website Description
                </label>
                <textarea
                  name="description"
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-surface-light border-neutral-200 text-brand-black shadow-sm focus:border-brand-black focus:ring-brand-black/10 sm:text-sm"
                  required
                />
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="language" className="block text-sm font-medium text-brand-black">
                    Primary Language
                  </label>
                  <select
                    name="language"
                    id="language"
                    value={formData.language}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md bg-surface-light border-neutral-200 text-brand-black shadow-sm focus:border-brand-black focus:ring-brand-black/10 sm:text-sm"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="it">Italian</option>
                    <option value="pt">Portuguese</option>
                    <option value="ru">Russian</option>
                    <option value="zh">Chinese</option>
                    <option value="ja">Japanese</option>
                    <option value="ko">Korean</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="contactEmail" className="block text-sm font-medium text-brand-black">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    name="contactEmail"
                    id="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md bg-surface-light border-neutral-200 text-brand-black shadow-sm focus:border-brand-black focus:ring-brand-black/10 sm:text-sm"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="allowTraining" className="block text-sm font-medium text-brand-black">
                    Allow Training
                  </label>
                  <select
                    name="allowTraining"
                    id="allowTraining"
                    value={formData.allowTraining}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md bg-surface-light border-neutral-200 text-brand-black shadow-sm focus:border-brand-black focus:ring-brand-black/10 sm:text-sm"
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="partial">Partial (with conditions)</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="allowCrawling" className="block text-sm font-medium text-brand-black">
                    Allow Crawling
                  </label>
                  <select
                    name="allowCrawling"
                    id="allowCrawling"
                    value={formData.allowCrawling}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md bg-surface-light border-neutral-200 text-brand-black shadow-sm focus:border-brand-black focus:ring-brand-black/10 sm:text-sm"
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="partial">Partial (with conditions)</option>
                  </select>
                </div>
              </div>

              <div>
                  <label htmlFor="additionalRules" className="block text-sm font-medium text-brand-black">
                  Additional Rules (Optional)
                </label>
                <textarea
                  name="additionalRules"
                  id="additionalRules"
                  rows={4}
                  value={formData.additionalRules}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-surface-light border-neutral-200 text-brand-black shadow-sm focus:border-brand-black focus:ring-brand-black/10 sm:text-sm"
                  placeholder="Add any specific rules or conditions for AI interaction with your content..."
                />
              </div>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={handleDownload}
                  className="inline-flex items-center gap-x-2 px-4 py-2.5 text-sm font-semibold shadow-sm btn-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  <DocumentTextIcon className="h-5 w-5" aria-hidden="true" />
                  Generate llms.txt
                  <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </main>
  )
}

export default Generator 